import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Megaphone,
  Building2,
  MapPin,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { Toaster } from "react-hot-toast";
import api from "../interceptors.js";
import { useConfig } from "../../context/configContext.js";
import "../../App.css";

const Campaigns = () => {
  const { config } = useConfig();
  const cpoId = config.customer_id;

  const [advertisers, setAdvertisers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter
  const [filterAdvertiser, setFilterAdvertiser] = useState("");

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    campaignId: "",
    campaignName: "",
    advertiserId: "",
    startDate: "",
    expiry: "",
    stationIds: "",
  });

  // Stations collapse
  const [expandedStations, setExpandedStations] = useState({});

  const isMobile = window.innerWidth < 768;

  // ─── Fetch Advertisers ───
  const fetchAdvertisers = async () => {
    if (!cpoId) return;
    try {
      const res = await api.get(`/api/advertisers?cpo_id=${cpoId}`);
      setAdvertisers(res.data?.advertisers || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ─── Fetch Campaigns ───
  const fetchCampaigns = async () => {
    if (!cpoId) return;
    setLoading(true);
    try {
      // Fetch all campaigns via advertiser_ids
      const advRes = await api.get(`/api/advertisers?cpo_id=${cpoId}`);
      const advs = advRes.data?.advertisers || [];
      setAdvertisers(advs);

      const allCampaigns = [];
      for (const adv of advs) {
        try {
          const campRes = await api.get(
            `/api/campaigns?adv_id=${adv.advertiser_id}`,
          );
          const camps = campRes.data?.campaigns || [];
          for (const c of camps) {
            allCampaigns.push({
              ...c,
              advertiser_name: adv.advertiser_name,
            });
          }
        } catch (err) {
          console.error(`Failed to fetch campaigns for ${adv.advertiser_id}`, err);
        }
      }
      setCampaigns(allCampaigns);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [cpoId]);

  // ─── Filtered list ───
  const filteredCampaigns = useMemo(() => {
    if (!filterAdvertiser) return campaigns;
    return campaigns.filter((c) => c.advertiser_id === filterAdvertiser);
  }, [campaigns, filterAdvertiser]);

  // ─── Save (Create / Update) ───
  const handleSave = async () => {
    if (!form.campaignName.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    if (!form.advertiserId) {
      toast.error("Select an advertiser");
      return;
    }

    try {
      if (editingId) {
        await api.put(`/api/campaigns/${editingId}`, {
          campaignName: form.campaignName,
          startDate: form.startDate || null,
          expiry: form.expiry || null,
          stationIds: form.stationIds
            ? form.stationIds.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
        });
        toast.success("Campaign updated");
      } else {
        if (!form.campaignId.trim()) {
          toast.error("Campaign ID is required");
          return;
        }
        await api.post("/api/campaigns", {
          campaignId: form.campaignId,
          campaignName: form.campaignName,
          advertiserId: form.advertiserId,
          startDate: form.startDate || null,
          expiry: form.expiry || null,
          stationIds: form.stationIds
            ? form.stationIds.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
        });
        toast.success("Campaign created");
      }
      resetForm();
      fetchCampaigns();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Save failed");
    }
  };

  // ─── Delete ───
  const handleDelete = async (campId) => {
    if (!window.confirm(`Delete campaign ${campId}? Media will be disassociated.`)) return;
    try {
      await api.delete(`/api/campaigns/${campId}`);
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  // ─── Edit ───
  const startEdit = (camp) => {
    setEditingId(camp.campaign_id);
    setForm({
      campaignId: camp.campaign_id,
      campaignName: camp.campaign_name,
      advertiserId: camp.advertiser_id,
      startDate: formatDateForInput(camp.start_date),
      expiry: formatDateForInput(camp.expiry),
      stationIds: (camp.station_ids || []).join(", "),
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({
      campaignId: "",
      campaignName: "",
      advertiserId: "",
      startDate: "",
      expiry: "",
      stationIds: "",
    });
  };

  // ─── Helpers ───
  const getAdvertiserName = (advId) => {
    const adv = advertisers.find((a) => a.advertiser_id === advId);
    return adv?.advertiser_name || advId;
  };

  const toggleStations = (campId) => {
    setExpandedStations((prev) => ({ ...prev, [campId]: !prev[campId] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl text-slate-50 p-2 sm:p-4">
      <Toaster position={isMobile ? "bottom-center" : "top-right"} />

      {/* Header */}
      <div className="mb-6 rounded-lg border border-cyan-100/40 bg-white/95 p-5 text-slate-900 shadow-2xl shadow-blue-950/20 backdrop-blur sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-sky-700">
              Campaign Management
            </p>
            <h1 className="text-2xl font-black text-slate-900 md:text-3xl">
              Campaigns
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage ad campaigns across your charging stations
            </p>
          </div>

          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-black text-white shadow transition hover:bg-sky-700"
          >
            <Plus size={18} />
            New Campaign
          </button>
        </div>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="mb-6 rounded-lg border border-cyan-100/25 bg-slate-950/30 p-5 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-black text-white">
              {editingId ? "Edit Campaign" : "New Campaign"}
            </h2>
            <button onClick={resetForm} className="text-cyan-100/60 hover:text-white transition">
              <X size={20} />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Campaign ID */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Campaign ID
              </label>
              <input
                type="text"
                placeholder="e.g. CMP001"
                value={form.campaignId}
                disabled={!!editingId}
                onChange={(e) => setForm({ ...form, campaignId: e.target.value })}
                className={`min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20 ${
                  editingId ? "cursor-not-allowed opacity-50" : ""
                }`}
              />
            </div>

            {/* Campaign Name */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Campaign Name
              </label>
              <input
                type="text"
                placeholder="e.g. KFC Summer Combo"
                value={form.campaignName}
                onChange={(e) => setForm({ ...form, campaignName: e.target.value })}
                className="min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>

            {/* Advertiser Dropdown */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Advertiser
              </label>
              <select
                value={form.advertiserId}
                disabled={!!editingId}
                onChange={(e) => setForm({ ...form, advertiserId: e.target.value })}
                className={`min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20 ${
                  editingId ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                <option value="" className="bg-slate-800">— Select Advertiser —</option>
                {advertisers.map((adv) => (
                  <option key={adv.advertiser_id} value={adv.advertiser_id} className="bg-slate-800">
                    {adv.advertiser_name} ({adv.advertiser_id})
                  </option>
                ))}
              </select>
            </div>

            {/* Station IDs */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Station IDs
              </label>
              <input
                type="text"
                placeholder="e.g. UFC001, UFC002"
                value={form.stationIds}
                onChange={(e) => setForm({ ...form, stationIds: e.target.value })}
                className="min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20"
              />
              <p className="mt-1 text-[10px] text-cyan-100/40">Comma-separated station IDs this campaign plays on</p>
            </div>

            {/* Start Date */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Start Date
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                End Date
              </label>
              <input
                type="date"
                value={form.expiry}
                onChange={(e) => setForm({ ...form, expiry: e.target.value })}
                className="min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 flex gap-3">
            <button
              onClick={handleSave}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-sky-600 px-5 py-2 text-sm font-black text-white shadow transition hover:bg-sky-700"
            >
              <Save size={16} />
              {editingId ? "Update" : "Create"}
            </button>
            <button
              onClick={resetForm}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-white/10 px-5 py-2 text-sm font-black text-white/80 transition hover:bg-white/20"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      <div className="rounded-lg border border-cyan-100/25 bg-slate-950/20 p-4 shadow-xl shadow-blue-950/10 backdrop-blur sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-cyan-100/80">
              Directory
            </p>
            <h2 className="m-0 text-xl font-black text-white">
              All Campaigns
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Filter by advertiser */}
            <select
              value={filterAdvertiser}
              onChange={(e) => setFilterAdvertiser(e.target.value)}
              className="min-h-9 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white outline-none focus:border-sky-400"
            >
              <option value="" className="bg-slate-800">All Advertisers</option>
              {advertisers.map((adv) => (
                <option key={adv.advertiser_id} value={adv.advertiser_id} className="bg-slate-800">
                  {adv.advertiser_name}
                </option>
              ))}
            </select>

            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black text-cyan-200">
              {filteredCampaigns.length} total
            </span>
          </div>
        </div>

        {loading && (
          <p className="text-sm text-cyan-100/60 italic">Loading...</p>
        )}

        {!loading && filteredCampaigns.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Megaphone size={48} className="mb-3 text-cyan-100/20" />
            <p className="text-sm font-semibold text-cyan-100/50">
              No campaigns found
            </p>
            <p className="text-xs text-cyan-100/30">
              Click "New Campaign" to create your first ad campaign
            </p>
          </div>
        )}

        <div className="space-y-2">
          {filteredCampaigns.map((camp) => (
            <div
              key={camp.campaign_id}
              className="group rounded-lg border border-cyan-100/15 bg-white/5 px-4 py-3 transition hover:border-cyan-100/30 hover:bg-white/10"
            >
              <div className="flex flex-wrap items-center gap-3">
                {/* Icon */}
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-900/40">
                  <Megaphone size={18} className="text-purple-400" />
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">
                    {camp.campaign_name}
                  </p>
                  <p className="text-xs text-cyan-100/50">
                    {camp.advertiser_name || camp.advertiser_id} · ID: {camp.campaign_id}
                  </p>
                </div>

                {/* Date badges */}
                <div className="hidden sm:flex items-center gap-2">
                  {camp.start_date && (
                    <span className="rounded-full bg-emerald-900/40 px-2.5 py-0.5 text-[10px] font-black text-emerald-300">
                      From {formatDate(camp.start_date)}
                    </span>
                  )}
                  {camp.expiry && (
                    <span className="rounded-full bg-rose-900/40 px-2.5 py-0.5 text-[10px] font-black text-rose-300">
                      Until {formatDate(camp.expiry)}
                    </span>
                  )}
                </div>

                {/* Station count */}
                {camp.station_ids?.length > 0 && (
                  <button
                    onClick={() => toggleStations(camp.campaign_id)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-900/40 px-2.5 py-0.5 text-[10px] font-black text-sky-300 transition hover:bg-sky-900/60"
                  >
                    <MapPin size={10} />
                    {camp.station_ids.length} station{camp.station_ids.length !== 1 ? "s" : ""}
                    <ChevronDown
                      size={10}
                      className={`transition ${expandedStations[camp.campaign_id] ? "rotate-180" : ""}`}
                    />
                  </button>
                )}

                {/* Actions */}
                <div className="flex shrink-0 gap-1.5 opacity-0 transition group-hover:opacity-100">
                  <button
                    onClick={() => startEdit(camp)}
                    className="rounded-lg bg-sky-600/20 p-2 text-sky-300 transition hover:bg-sky-600/40"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(camp.campaign_id)}
                    className="rounded-lg bg-red-600/20 p-2 text-red-400 transition hover:bg-red-600/40"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Expanded stations list */}
              {expandedStations[camp.campaign_id] && camp.station_ids?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 border-t border-white/10 pt-2">
                  {camp.station_ids.map((sid) => (
                    <span
                      key={sid}
                      className="rounded-md bg-slate-800/80 px-2 py-0.5 text-[11px] font-semibold text-cyan-200"
                    >
                      {sid}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Campaigns;
